import { ChangeDetectorRef, Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { defineLocale } from 'ngx-bootstrap/chronos';
import { BsDatepickerConfig, BsLocaleService } from 'ngx-bootstrap/datepicker';
import { enGbLocale } from 'ngx-bootstrap/locale';

import { StorageService } from 'app/core/storage/storage.service';
import { DateSelectionType } from 'app/shared/components/date-time-selector/date-selection-type';
import { DateFormats } from 'app/shared/constants/date-formats';
import { TranslationService } from 'app/core/internationalization/translation.service';

@Component({
  selector: 'kh-date-time-selector',
  templateUrl: './date-time-selector.component.html',
  styleUrls: ['./date-time-selector.component.less'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DateTimeSelectorComponent),
    multi: true
  }],
  standalone: false
})
export class DateTimeSelectorComponent implements ControlValueAccessor, OnInit {
  public date: Date | string | null = null;
  public dateFormatLabel = 'yyyy-MM-dd';

  @Input() showButtons = false;
  @Input() noMinDate = false;
  @Input() displayError = false;
  @Input() allowEmptyDate = false;
  @Input() disabled = false;
  @Input() onlyFutureDate = false;
  @Input() testId = '';
  @Input() minDateOverride?: Date | string;
  @Input() tooltipError: string | null = null;
 
  public selectionType = '';
  public dateSelectionTypeEnum = DateSelectionType;
  public dateFormat = DateFormats.SHORT_DATE;
  public minDate = this.storageService.getDate();
  public setMinDate = false;
  public error = false;
  public datePickerIsOpened = false;
  public datePickerConfig: Partial<BsDatepickerConfig> = {};

  constructor(
    private readonly storageService: StorageService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly _translationService: TranslationService,
    private localeService: BsLocaleService) {
    this.datePickerConfig = {
      adaptivePosition: true,
      showTodayButton: true,
      showClearButton: true,
      clearPosition: 'center',
      containerClass: ' kh-theme all-bottom-buttons-show',
      dateInputFormat: 'YYYY-MM-DD',
      // minDate will be set in ngOnInit when inputs are finalized
      minDate: undefined,
      todayButtonLabel: this._translationService.instant('DATE_TIME_SELECTOR.TODAY'),
      clearButtonLabel: this._translationService.instant('DATE_TIME_SELECTOR.CLEAR')
    };

    const customLocale = {
      ...enGbLocale,
      months: this._translationService.instant('DATE_TIME_SELECTOR.JANUARY_FEBRUARY_MARCH_APRIL_MAY').split(',').map((x: string) => x.trim()),
      weekdaysShort: this._translationService.instant('DATE_TIME_SELECTOR.SUN_MON_TUE_WED_THU').split(',').map((x: string) => x.trim())
    };

    defineLocale('custom', customLocale);
    this.localeService.use('custom');
  }

  ngOnInit(): void {
    this.selectionType = this.dateSelectionTypeEnum.Future;
    // determine min date: prefer override, else storageService
    if (this.minDateOverride) {
      this.minDate = this.minDateOverride instanceof Date ? this.minDateOverride : new Date(this.minDateOverride);
    }

    // if onlyFutureDate is requested, set minDate to tomorrow
    if (this.onlyFutureDate) {
      const today = this.storageService.getDate();
      this.minDate = new Date(today.getTime() + 24 * 60 * 60 * 1000);
    }

    this.setMinDate = !this.noMinDate || this.onlyFutureDate;
    // ensure datepicker config reflects final minDate
    this.datePickerConfig.minDate = this.setMinDate ? this.minDate : undefined;
    this.dateFormatLabel = this._translationService.instant('DATE_TIME_SELECTOR.YYYYMMDD');
  }

  public dateChanged(value: any): void {
    this.date = value;
    this.onDateChange(value);
  }

  public setType(type: DateSelectionType): void {
    if (type === this.dateSelectionTypeEnum.Immediate) {
      this.selectionType = this.dateSelectionTypeEnum.Immediate;
      this.dateChanged(this.minDate);
      return;
    }

    this.selectionType = this.dateSelectionTypeEnum.Future;
  }

  public registerOnChange(fn: any): void {
    this.onDateChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public writeValue(value: Date | string | null): void {
    this.date = value;
  }

  public toggleOpeningDatepicker(): void {
    this.datePickerIsOpened = true;
    this.changeDetectorRef.detectChanges();
  }

  public openDatepickerIfEnabled(dp: any) {
    if (!(this.selectionType === this.dateSelectionTypeEnum.Immediate || this.disabled)) {
      dp.show();
    }
  }

  private onDateChange = (date: any) => {
    if (!date && this.allowEmptyDate) {
      this.date = this.minDate;
    }
  };

  private onTouched = (date: any) => {
  };
}
